import {
  Add,
  Delete,
  Edit,
  LockOpen,
  LockOutlined,
  OpenInNew,
} from '@mui/icons-material';
import { Link, Tooltip } from '@mui/material';
import {
  DataGridPremiumProps,
  GridActionsCellItem,
  GridColDef,
  GridColumnGroupingModel,
  GridPinnedColumnFields,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmDialog } from '~/components/DialogsProvider';
import {
  EmployeeCellComponent,
  dateCellValueFormatter,
  enumCellValueGetter,
} from '~/components/SolaForceDataGrid';
import {
  EmployeeCollaborationTask,
  EmployeeCollaborationTaskList,
  EmployeeCollaborationWorklistStatus,
} from '~/data/collaboration';
import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { AbsenceHistoryGridTreeDataGroupingCell } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceHistoryDialog/AbsenceHistoryDialogContent/AbsenceHistoryGridTreeDataGroupingCell';
import { WorklistCheckboxCell } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/components/WorklistCheckboxCell';
import {
  WorkListTableRow,
  WorkListTableRowType,
  deleteEmployeeWorkListTask,
  deleteEmployeeWorklist,
  resolveNextTaskSeq,
  updateEmployeeTemplateTasks,
  workListRoleAccess,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import { WorklistTaskFormData } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';

type ListDataPermissions = {
  canEdit: boolean;
  canAddTask: boolean;
  canDelete: boolean;
  canLock: boolean;
};

type TaskDataPermissions = {
  canEdit: boolean;
  canDelete: boolean;
};

type PropsType = {
  onUpdate: () => void;
  onEditWorklist: (worklist: EmployeeCollaborationTaskList) => void;
  onEditTask: (task: EmployeeCollaborationTask) => void;
  onAddTask: (taskData: WorklistTaskFormData) => void;
  getNewTaskFormData: (props: {
    templateId?: number;
    worklistId?: string;
    seq?: number;
  }) => WorklistTaskFormData | undefined;
};

type UseWorklistTableColumnsOutput = {
  columns: GridColDef<WorkListTableRow>[];
  groupingColDef: DataGridPremiumProps['groupingColDef'];
  columnGroupingModel: GridColumnGroupingModel;
  pinnedColumns?: GridPinnedColumnFields;
};

export const useWorklistTableColumns = (
  props: PropsType,
): UseWorklistTableColumnsOutput => {
  const {
    onUpdate,
    onEditTask,
    onAddTask,
    onEditWorklist,
    getNewTaskFormData,
  } = props;

  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const { selectedEmployeeId: empId } = useSelectedEmployeeId();

  const { enqueueSnackbar } = useSnackbar();

  const { getEnums } = useAllEnums();
  const { taskTypes } = useMemo(() => {
    return {
      taskTypes: getEnums(EnumListKey.COLLAB_TASK_TYPE),
    };
  }, [getEnums]);

  const { getEmployeeById } = useAllEmployees();

  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const rolePermissions = useMemo((): {
    list: ListDataPermissions;
    task: TaskDataPermissions;
  } => {
    const canCreateWorkList = hasRoleAccessOverEmployee(
      workListRoleAccess.canCreateWorkList,
    );

    return {
      list: {
        canEdit: canCreateWorkList,
        canAddTask: canCreateWorkList,
        canDelete: canCreateWorkList,
        canLock: canCreateWorkList,
      },
      task: {
        canEdit: canCreateWorkList,
        canDelete: canCreateWorkList,
      },
    };
  }, [hasRoleAccessOverEmployee]);

  const getListPermissions = useCallback(
    (listData?: EmployeeCollaborationTaskList): ListDataPermissions => {
      if (!listData) {
        return {
          canEdit: false,
          canAddTask: false,
          canDelete: false,
          canLock: false,
        };
      }

      return rolePermissions.list;
    },
    [rolePermissions],
  );

  const getTaskPermissions = useCallback(
    (taskData?: EmployeeCollaborationTask): TaskDataPermissions => {
      if (!taskData) {
        return {
          canEdit: false,
          canDelete: false,
        };
      }

      return rolePermissions.task;
    },
    [rolePermissions],
  );

  const groupingColDef: DataGridPremiumProps['groupingColDef'] = {
    headerName: '',
    width: 50,
    renderCell: (params) => (
      <AbsenceHistoryGridTreeDataGroupingCell {...params} />
    ),
  };

  const onDeleteWorklist = useCallback(
    (worklist: EmployeeCollaborationTaskList) => {
      return confirm(t('listDeleteConfirmationMessage'), (confirmed) => {
        if (confirmed) {
          deleteEmployeeWorklist(worklist)
            .then(() => {
              enqueueSnackbar(t('listDeleted'), {
                variant: 'success',
              });
              onUpdate();
            })
            .catch((err) => {
              enqueueSnackbar(t('deleteFailed'), {
                variant: 'error',
              });
              console.log('onDeleteWorklist', err);
            });
        }
      });
    },
    [confirm, enqueueSnackbar, onUpdate, t],
  );

  const onDeleteWorklistTask = useCallback(
    (taskData: EmployeeCollaborationTask) => {
      return confirm(t('taskDeleteConfirmationMessage'), (confirmed) => {
        if (confirmed) {
          deleteEmployeeWorkListTask(taskData)
            .then(() => {
              enqueueSnackbar(t('taskDeleted'), {
                variant: 'success',
              });
              onUpdate();
            })
            .catch((err) => {
              enqueueSnackbar(t('deleteFailed'), {
                variant: 'error',
              });
              console.log('onDeleteWorklistTask', err);
            });
        }
      });
    },
    [confirm, enqueueSnackbar, onUpdate, t],
  );

  const onToggleLockedState = useCallback(
    (listData: EmployeeCollaborationTaskList) => {
      updateEmployeeTemplateTasks(empId, {
        ...listData,
        fEmpWorklistIsLocked: !listData.fEmpWorklistIsLocked,
      })
        .then(() => {
          enqueueSnackbar(t('listUpdated'), {
            variant: 'success',
          });
          onUpdate();
        })
        .catch((err) => {
          enqueueSnackbar(t('failedToUpdate'), {
            variant: 'error',
          });
          console.log('onToggleLockedState', err);
        });
    },
    [empId, onUpdate, enqueueSnackbar, t],
  );

  const columnConfigs = useMemo((): GridColDef<WorkListTableRow>[] => {
    return [
      {
        field: 'subject',
        headerName: t('laSubject'),
        flex: 1,
      },
      {
        field: 'description',
        headerName: t('laDescription'),
        flex: 1,
        cellClassName: 'multiline-description',
      },
      {
        field: 'link',
        headerName: t('link'),
        minWidth: 60,
        flex: 0.1,
        renderCell: ({ value }) => {
          return !!value ? (
            <Tooltip title={value} placement={'right'} arrow={true}>
              <Link href={value} target={'_blank'}>
                <OpenInNew />
              </Link>
            </Tooltip>
          ) : (
            <></>
          );
        },
      },
      {
        field: 'type',
        headerName: t('laType'),
        flex: 0.5,
        valueFormatter: enumCellValueGetter(taskTypes),
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        minWidth: 120,
        flex: 0.3,
        valueGetter: (value, row) => {
          return row.rowType === WorkListTableRowType.List ? value : undefined;
        },
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'dueDate',
        headerName: t('laDueDate'),
        minWidth: 120,
        flex: 0.3,
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'responsible',
        headerName: t('responsible'),
        minWidth: 180,
        flex: 0.5,
        renderCell: (params) => <EmployeeCellComponent {...params} />,
      },
      {
        field: 'done',
        headerName: t('taskDone'),
        type: 'boolean',
        flex: 0.3,
        disableReorder: true,
        sortable: false,
        renderCell: (params) => {
          return <WorklistCheckboxCell {...params} onUpdate={onUpdate} />;
        },
        colSpan: (_value, row) => {
          return row.rowType === WorkListTableRowType.List ? 2 : 1;
        },
      },
      {
        field: 'skipped',
        headerName: t('taskSkipped'),
        type: 'boolean',
        flex: 0.3,
        disableReorder: true,
        sortable: false,
        renderCell: (params) => {
          return <WorklistCheckboxCell {...params} onUpdate={onUpdate} />;
        },
      },
      {
        field: 'lastEdited',
        headerName: t('laLastEdited'),
        flex: 0.5,
        renderCell: ({ value, row }) => {
          const { rowType, taskData } = row;

          const getCellInfo = (employee?: Employees) => {
            if (!employee || !value) {
              return '';
            }

            return `${employee?.fEmpFirstName} ${
              employee?.fEmpLastName
            } ${moment(value).format('L')}`;
          };

          switch (rowType) {
            case WorkListTableRowType.Task:
              return getCellInfo(
                getEmployeeById(taskData?.taskAudit.fTaskUpdatedEmpId),
              );
            case WorkListTableRowType.List:
            default:
              return '';
          }
        },
      },
      {
        field: 'rowType',
        headerName: '',
        type: 'actions',
        width: 50,
        getActions: (params: GridRowParams<WorkListTableRow>) => {
          const { row } = params;
          const isLocked = row.locked;
          const menuActions: JSX.Element[] = [];

          switch (row.rowType) {
            case WorkListTableRowType.List: {
              const { listData, templateData } = row;
              const access = getListPermissions(listData);
              const nextSeq = resolveNextTaskSeq(
                !!listData?.fEmpWorklistTasks ? listData.fEmpWorklistTasks : [],
              );

              const newTaskData = getNewTaskFormData({
                templateId: listData?.fEmpWorklistTemplateId,
                worklistId: listData?.fEmpWorklistUid,
                seq: nextSeq,
              });

              const showAddTask =
                !!newTaskData &&
                access.canAddTask &&
                !!templateData?.fCollaborationTemplateIsActive;

              if (showAddTask) {
                menuActions.push(
                  <GridActionsCellItem
                    icon={<Add />}
                    label={t('addTask')}
                    className="textPrimary"
                    onClick={() => {
                      onAddTask(newTaskData);
                    }}
                    showInMenu={true}
                    disabled={isLocked}
                  />,
                );
              }
              if (!!listData) {
                if (access.canEdit) {
                  menuActions.push(
                    <GridActionsCellItem
                      icon={<Edit />}
                      label={t('edit')}
                      className="textPrimary"
                      onClick={() => {
                        onEditWorklist(listData);
                      }}
                      showInMenu={true}
                      disabled={row.locked}
                    />,
                  );
                }

                if (access.canLock) {
                  const isDisabled = !isLocked
                    ? row.listData?.fEmpWorklistStatus !==
                      EmployeeCollaborationWorklistStatus.Completed
                    : false;

                  menuActions.push(
                    <GridActionsCellItem
                      icon={isLocked ? <LockOpen /> : <LockOutlined />}
                      label={isLocked ? t('unlock') : t('lock')}
                      className="textPrimary"
                      onClick={() => {
                        onToggleLockedState(listData);
                      }}
                      showInMenu={true}
                      disabled={isDisabled}
                    />,
                  );
                }

                if (access.canDelete) {
                  menuActions.push(
                    <GridActionsCellItem
                      icon={<Delete />}
                      label={t('delete')}
                      className="textPrimary"
                      onClick={() => {
                        onDeleteWorklist(listData);
                      }}
                      showInMenu={true}
                      disabled={isLocked}
                    />,
                  );
                }
              }

              return menuActions;
            }
            case WorkListTableRowType.Task: {
              const { taskData } = row;

              if (!!taskData && !!taskData.fTaskId) {
                const access = getTaskPermissions(taskData);
                const isTemplateTask = !!taskData.taskCore.fTaskTemplateId;

                if (row.locked) {
                  return [];
                }

                if (access.canEdit) {
                  menuActions.push(
                    <GridActionsCellItem
                      icon={<Edit />}
                      label={t('edit')}
                      className="textPrimary"
                      onClick={() => {
                        onEditTask(taskData);
                      }}
                      showInMenu={true}
                      disabled={row.locked}
                    />,
                  );
                }

                if (access.canDelete && !isTemplateTask) {
                  menuActions.push(
                    <GridActionsCellItem
                      icon={<Delete />}
                      label={t('delete')}
                      className="textPrimary"
                      onClick={() => {
                        onDeleteWorklistTask(taskData);
                      }}
                      showInMenu={true}
                      disabled={row.locked}
                    />,
                  );
                }
              }

              return menuActions;
            }
          }
        },
      },
    ];
  }, [
    t,
    taskTypes,
    onUpdate,
    onEditWorklist,
    onEditTask,
    onDeleteWorklist,
    onDeleteWorklistTask,
    onToggleLockedState,
    getEmployeeById,
    getNewTaskFormData,
    onAddTask,
    getListPermissions,
    getTaskPermissions,
  ]);

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: t('status'),
      children: [{ field: 'skipped' }, { field: 'done' }],
    },
  ];

  const pinnedColumns: GridPinnedColumnFields = {
    right: ['rowType'],
  };

  return {
    columns: columnConfigs,
    groupingColDef,
    columnGroupingModel,
    pinnedColumns,
  };
};
